import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../../layouts/AdminLayout';
import { useCases } from '../../hooks';
import { LoadingOverlay } from '../../components/LoadingOverlay';
import { LayoutTemplate, Search } from 'lucide-react';
import styles from './LayoutsPage.module.css';

export const LayoutsPage: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = React.useState('');
  const { data, loading, error, updateFilter } = useCases();

  React.useEffect(() => {
    const debounce = setTimeout(() => {
      updateFilter('search', search || undefined);
    }, 300);
    return () => clearTimeout(debounce);
  }, [search, updateFilter]);

  const handleSelectCase = (id: string) => {
    navigate(`/admin/cases/${id}/edit?tab=layout`);
  };

  if (loading && !data) {
    return (
      <AdminLayout>
        <LoadingOverlay isLoading={true} />
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className={styles.container}>
          <div className={styles.error}>
            <h3>Error loading cases</h3>
            <p>{error.message}</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  const cases = data?.data?.items || [];

  return (
    <AdminLayout>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.titleSection}>
            <LayoutTemplate className={styles.titleIcon} />
            <div>
              <h1 className={styles.title}>Layout Builder</h1>
              <p className={styles.subtitle}>
                Select a case to design its layout
              </p>
            </div>
          </div>
          <div className={styles.searchWrapper}>
            <Search className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Search cases..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={styles.searchInput}
            />
          </div>
        </div>

        <div className={styles.grid}>
          {cases.map((caseItem) => (
            <button
              key={caseItem.id}
              className={styles.card}
              onClick={() => handleSelectCase(caseItem.id)}
            >
              <div className={styles.cardImage}>
                {caseItem.cover ? (
                  <img src={caseItem.cover} alt={caseItem.name} />
                ) : (
                  <div className={styles.cardImagePlaceholder}>
                    <LayoutTemplate />
                  </div>
                )}
              </div>
              <div className={styles.cardContent}>
                <h3 className={styles.cardTitle}>{caseItem.name}</h3>
                <p className={styles.cardClient}>{caseItem.clientName}</p>
              </div>
            </button>
          ))}
        </div>

        {cases.length === 0 && !loading && (
          <div className={styles.empty}>
            <LayoutTemplate className={styles.emptyIcon} />
            <h3 className={styles.emptyTitle}>No cases found</h3>
            <p className={styles.emptyDescription}>
              {search
                ? 'Try adjusting your search'
                : 'Create a case first to design its layout'}
            </p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};
